/**
 * enrichedAdapter — bridges the enriched S3 export (EnrichedTicket) to the existing
 * rule-based pipeline that operates on TicketsParsedDataItem.
 *
 * WHY: all scoring/aggregation (scoreTicket, buildSimHealthPreview, buildFullTrackerRows)
 * is written against TicketsParsedDataItem. By adapting each EnrichedTicket into that
 * shape we reuse 100% of the derived-field logic unchanged, and separately overlay the
 * LLM commentary (which the parsed shape has no place for) keyed by case_id.
 */
import moment from 'moment-timezone';
import { TicketsParsedDataItem, Ticket_Status } from 'src/components/fintech-ops/tickets/TicketsInterfaces';
import { FilterDateFormat_MMM_YYYY, OEEmptyStringMappings } from 'src/components/fintech-ops/FinTechOpsConstants';
import { convertUTCtoUserTimeZone } from 'src/utilities/DateTimeUtilities';
import { EnrichedTicket } from '../interfaces/enrichedTicketExport.types';

/** Split a ":a:b:c:" hierarchy concat into ordered names/logins (drops empties). */
const splitHierarchy = (concat: string | undefined): string[] =>
  (concat ?? '')
    .split(':')
    .map((s) => s.trim())
    .filter(Boolean);

/** Age in minutes from an ISO create date to `asOf` (for ticket_age_in_minutes). */
const ageMinutes = (createIso: string, asOf: moment.Moment): number => {
  const created = moment(createIso);
  if (!created.isValid()) return 0;
  return Math.max(0, asOf.diff(created, 'minutes'));
};

/** Resolve time in seconds = resolved_date - create_date (0 when not resolved/invalid). */
const resolveSeconds = (createIso: string, resolvedIso: string | null | undefined): number => {
  if (!resolvedIso) return 0;
  const created = moment(createIso);
  const resolved = moment(resolvedIso);
  if (!created.isValid() || !resolved.isValid()) return 0;
  return Math.max(0, resolved.diff(created, 'seconds'));
};

/**
 * Normalize an enriched `status` string into the Ticket_Status union WITHOUT inventing data.
 *  - Known union members (case-insensitive match) are returned canonicalized.
 *  - A present-but-out-of-union value (e.g. 'Closed', which the enriched vocabulary can carry and
 *    which downstream isResolved() treats as resolved via a lowercase check) is PRESERVED as-is —
 *    we do not coerce it to a wrong member, which would mis-classify it. This one residual case is
 *    the only cast, and it keeps the real string so status-based logic stays correct.
 *  - Empty/missing → 'Assigned' (a valid, neutral "active" member), NOT the out-of-union 'Open'
 *    the previous code force-cast.
 */
const KNOWN_STATUSES: Ticket_Status[] = ['Assigned', 'Pending', 'Resolved', 'Work In Progress'];
const normalizeStatus = (raw: string | null | undefined): Ticket_Status => {
  const s = (raw ?? '').trim();
  if (!s) return 'Assigned';
  const match = KNOWN_STATUSES.find((k) => k.toLowerCase() === s.toLowerCase());
  if (match) return match;
  // Out-of-union but real (e.g. 'Closed') — preserve the actual string so downstream
  // resolved/open classification (which lowercases) still behaves correctly.
  return s as Ticket_Status;
};

/**
 * Map one EnrichedTicket to the fields of TicketsParsedDataItem that scoring and
 * aggregation actually read. Fields the derived logic never touches are filled with
 * safe defaults so the object satisfies the type without inventing data.
 */
export const enrichedToParsed = (t: EnrichedTicket, asOf: moment.Moment): TicketsParsedDataItem => {
  const nameHier = splitHierarchy(t.manager_name_hierarchy_concat);
  const loginHier = splitHierarchy(t.manager_login_hierarchy_concat);
  // Fallbacks MUST match the run-data path (TicketsDataProcessing) EXACTLY — same shared
  // OEEmptyStringMappings constants AND the same `||` (falsy) trigger — so unassigned tickets
  // bucket into the same group across the enriched and run-data sources rather than splitting
  // aggregation/filtering. (assignee name+login both use Generic_Mapping = 'Unassigned'.)
  const assigneeName = t.assigned_to_individual_name || OEEmptyStringMappings.Generic_Mapping;
  const assigneeLogin = t.assignee_login || OEEmptyStringMappings.Generic_Mapping;
  // Convert the raw UTC instants to the user's timezone BEFORE deriving tz-dependent fields
  // (formatted month, ISO week, month number) — matching the run-data path so both sources bucket
  // consistently. Empty string stays empty (convertUTCtoUserTimeZone would otherwise yield an
  // "Invalid date" for a blank input).
  const createdUserTz = t.create_date ? convertUTCtoUserTimeZone(t.create_date) : '';
  const resolvedUserTz = t.resolved_date ? convertUTCtoUserTimeZone(t.resolved_date) : '';
  // assigned_date / modified_date are ALSO tz-converted to match the run-data path (which stores
  // convertUTCtoUserTimeZone(...) for both), so any consumer diffing them against another user-tz
  // field stays on a consistent basis across the enriched and run-data sources.
  const assignedUserTz = t.assigned_date ? convertUTCtoUserTimeZone(t.assigned_date) : '';
  const modifiedUserTz = t.modified_date ? convertUTCtoUserTimeZone(t.modified_date) : '';
  // Parse the converted values once; guard validity (a malformed input can survive conversion as an
  // "Invalid date" string) so formatted/week/month never emit "Invalid date" or NaN — consistent
  // with the isValid() guards in ageMinutes/resolveSeconds.
  const createdMoment = createdUserTz ? moment(createdUserTz) : null;
  const createdValid = !!createdMoment && createdMoment.isValid();
  const resolvedMoment = resolvedUserTz ? moment(resolvedUserTz) : null;
  const resolvedValid = !!resolvedMoment && resolvedMoment.isValid();
  return {
    team_name: t.team_name || OEEmptyStringMappings.Unassigned_Team,
    manager_name_hierarchy_concat: nameHier,
    // Manager = the assignee's DIRECT manager. The hierarchy concat starts at the
    // assignee's manager (index 0 = direct manager, 1 = grand-manager, ...), so use [0].
    // (Using [1] previously showed the manager's manager, collapsing many first-line
    // managers into a few senior ones and dropping real managers from the Manager view.)
    // Fallbacks use the SAME shared constants as run-data so unassigned tickets bucket identically.
    manager_name: nameHier[0] || OEEmptyStringMappings.Unassigned_Manager,
    manager_login_hierarchy_concat: loginHier,
    manager_login: loginHier[0] || OEEmptyStringMappings.Unassigned_Manager_Login,
    assignee_login: assigneeLogin,
    assignee_name: assigneeName,
    assigned_to_group: t.assigned_to_group ?? '',
    application_name: t.application_name || OEEmptyStringMappings.Unassigned_Application,
    create_date_UserTimeZone: createdUserTz,
    create_date_UserTimeZone_formatted: createdValid ? createdMoment.format(FilterDateFormat_MMM_YYYY) : '',
    // For UNRESOLVED/invalid resolved dates, emit the SAME 'Invalid date' string run-data produces
    // (run-data does moment(null).format(...) → 'Invalid date'). Consumers like
    // getResolvedMonthUniqueList exclude unresolved rows via `!== 'invalid date'`; emitting '' here
    // would slip unresolved tickets into resolved-month lists once enriched data flows through the
    // shared (drop-in) path. Matching the exact string keeps that filter working across sources.
    resolved_date_UserTimeZone_formatted: resolvedValid
      ? resolvedMoment.format(FilterDateFormat_MMM_YYYY)
      : moment.invalid().format(FilterDateFormat_MMM_YYYY),
    // Derive week/month from the USER-TZ value (not the raw UTC instant) so enriched-sourced tickets
    // bucket identically to the run-data path (TicketsDataProcessing derives these from the
    // tz-converted createdDate). Deriving from UTC would drift a ticket into a different week/month
    // near a date boundary, splitting aggregation across the two sources.
    created_on_week: createdValid ? createdMoment.isoWeek() : 0,
    created_on_month: createdValid ? createdMoment.month() + 1 : 0,
    ticket_age_in_minutes: ageMinutes(t.create_date, asOf),
    resolved_ticket_age_in_seconds: resolveSeconds(t.create_date, t.resolved_date),
    last_modified_by: t.last_modified_by ?? '',
    modified_date: modifiedUserTz,
    assigned_date: assignedUserTz,
    case_id: t.case_id ?? '',
    short_description: t.short_description ?? '',
    impact: (t.impact ?? '').toString(),
    resolution_type: t.resolution_type || OEEmptyStringMappings.Not_Entered,
    resolved_date: resolvedUserTz || null,
    sim_issue_alias: t.sim_issue_alias ?? '',
    status: normalizeStatus(t.status),
    total_time_spent: t.total_time_spent ?? 0,
    last_correspondence_date: t.last_correspondence_date ?? null,
    last_human_correspondence_date: t.last_human_correspondence_date ?? null,
    correspondence_count: t.correspondence_count ?? null,
    // Not in the enriched export (Sev-2 first-response SLA is a run-data/live concept); null = N/A.
    first_response_age_in_seconds: null,
    // Not in the enriched export (resolved-business-age is a run-data concept); 0 = not computed here.
    resolved_business_age_in_seconds: 0
  };
};

export const enrichedListToParsed = (tickets: EnrichedTicket[], asOf: moment.Moment): TicketsParsedDataItem[] =>
  tickets.map((t) => enrichedToParsed(t, asOf));
